import { db, storage } from '../firebase';
import { doc, getDoc, collection, addDoc, getDocs, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  PetProfile,
  OnboardingState,
  OnboardingStep,
  OnboardingConfig,
  OnboardingCallbacks,
  ImageAnalysisResult
} from '../types/onboarding';
import { validateImageFile } from './petImageAnalysis';
import {
  initializeOnboardingConversation,
  processOnboardingMessage,
  ConversationContext
} from './onboardingAI';
import { calculateBirthdayFromAge } from '../utils/birthdayCalculation';

export class OnboardingService {
  private config: OnboardingConfig;
  private callbacks: OnboardingCallbacks;
  private state: OnboardingState;
  private conversationContext: ConversationContext | null = null;

  constructor(
    config: Partial<OnboardingConfig> = {},
    callbacks: OnboardingCallbacks = {}
  ) {
    this.config = {
      enableAI: false,
      maxRetries: 3,
      timeout: 30000,
      supportedLanguages: ['en', 'zh-CN', 'es', 'fr'],
      ...config
    };

    this.callbacks = callbacks;

    this.state = {
      currentStep: OnboardingStep.WELCOME,
      petData: {},
      userLanguage: 'en',
      isLoading: false
    };
  }

  /**
   * Initializes the onboarding process for a new user
   * @param userId - Firebase Auth user ID
   * @param userLanguage - User's preferred language
   * @returns Promise<void>
   */
  async initialize(userId: string, userLanguage: 'en' | 'zh-CN' | 'es' | 'fr' = 'en'): Promise<void> {
    try {
      this.state = {
        currentStep: OnboardingStep.WELCOME,
        petData: {
          userId,
          type: 'dog', // Default, will be updated
          isActive: true,
          createdAt: serverTimestamp() as any,
          updatedAt: serverTimestamp() as any
        },
        userLanguage,
        isLoading: false
      };

      this.callbacks.onStepChange?.(OnboardingStep.WELCOME);

      // Initialize AI conversation in background if enabled
      if (this.config.enableAI) {
        // Don't await this - let it run in background
        this.initializeAIConversation(userLanguage);
      }

    } catch (error) {
      console.error('Error initializing onboarding:', error);
      this.state.error = 'Failed to initialize onboarding process';
      this.callbacks.onError?.(this.state.error);
    }
  }

  /**
   * Initialize AI conversation in background
   */
  private async initializeAIConversation(userLanguage: 'en' | 'zh-CN' | 'es' | 'fr'): Promise<void> {
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('AI initialization timeout')), 10000); // 10 second timeout
      });

      const threadId = await Promise.race([
        initializeOnboardingConversation(userLanguage),
        timeoutPromise
      ]);

      this.conversationContext = {
        userLanguage,
        currentStep: OnboardingStep.WELCOME,
        petData: this.state.petData,
        threadId
      };
    } catch (error) {
      console.warn('AI conversation initialization failed, continuing without AI features:', error);
    }
  }

  /**
   * Starts the onboarding flow (moves from welcome to photo upload)
   * @returns Promise<void>
   */
  async startOnboarding(): Promise<void> {
    this.updateStep(OnboardingStep.PHOTO_UPLOAD);
  }

  /**
   * Processes photo upload and initiates AI analysis
   * @param file - Uploaded image file
   * @param petType - Type of pet ('dog' or 'cat')
   * @returns Promise<ImageAnalysisResult | null>
   */
  async uploadPetPhoto(file: File, petType: 'dog' | 'cat' | 'other'): Promise<ImageAnalysisResult | null> {
    try {
      this.state.isLoading = true;
      this.state.error = undefined;

      // Validate file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Update pet type
      this.state.petData.type = petType;

      let imageUrl: string;
      
      try {
        // Try to upload to Firebase Storage
        imageUrl = await this.uploadImageToStorage(file);
        console.log('✅ Image uploaded to Firebase Storage successfully');
      } catch (storageError) {
        console.warn('⚠️ Firebase Storage upload failed, using fallback:', storageError);
        
        try {
          // Fallback: Convert image to compressed data URL for temporary storage
          imageUrl = await this.convertFileToCompressedDataURL(file);
          console.log('✅ Using compressed data URL fallback for image');
        } catch (fallbackError) {
          console.error('❌ Fallback conversion also failed:', fallbackError);
          throw new Error('Failed to process image. Please try a different image or skip photo upload.');
        }
      }

      // Update state with photo URL
      this.state.petData.profilePhoto = imageUrl;

      this.state.isLoading = false;

      // Move to confirm details step
      this.updateStep(OnboardingStep.CONFIRM_DETAILS);

      return null;

    } catch (error) {
      console.error('Error uploading pet photo:', error);
      this.state.isLoading = false;
      this.state.error = error instanceof Error ? error.message : 'Failed to upload photo';
      this.callbacks.onError?.(this.state.error);
      return null;
    }
  }

  /**
   * Updates pet profile data from user input
   * @param updates - Partial pet profile updates
   * @returns Promise<void>
   */
  async updatePetData(updates: Partial<PetProfile>): Promise<void> {
    try {
      this.state.petData = {
        ...this.state.petData,
        ...updates,
        updatedAt: serverTimestamp() as any
      };

      // Update conversation context if available
      if (this.conversationContext) {
        this.conversationContext.petData = this.state.petData;
      }

    } catch (error) {
      console.error('Error updating pet data:', error);
      this.callbacks.onError?.('Failed to update pet information');
    }
  }

  /**
   * Completes the onboarding process and saves the pet profile
   * @param petName - Final pet name
   * @returns Promise<PetProfile>
   */
  async completeOnboarding(petName: string): Promise<PetProfile> {
    try {
      this.state.isLoading = true;

      // Validate pet name
      if (!petName.trim()) {
        throw new Error('Pet name is required');
      }

      // Finalize pet data
      const finalPetData: Omit<PetProfile, 'id'> & { id?: string; userId: string; type: 'dog' | 'cat' | 'other' } = {
        ...this.state.petData,
        name: petName.trim(),
        userId: this.state.petData.userId || '',
        type: this.state.petData.type || 'dog',
        isActive: true, // Always set to true for new pets
        onboardingCompleted: true,
        onboardingStep: OnboardingStep.COMPLETED,
        createdAt: serverTimestamp() as any,
        updatedAt: serverTimestamp() as any
      };

      // Prepare data for Firestore from finalPetData (saving all fields)
      const firestoreData: any = {
        ...finalPetData,
        breed: finalPetData.breed || 'Unknown', // Provide fallback for breed
        age: finalPetData.age || 0, // Provide fallback for age
        gender: finalPetData.gender || 'unknown', // Provide fallback for gender
        isActive: true,
        onboardingCompleted: true,
        onboardingStep: OnboardingStep.COMPLETED,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        birthday: finalPetData.birthday || serverTimestamp(), // Use calculated birthday or fallback to serverTimestamp
        profilePhoto: finalPetData.profilePhoto || '', // Provide empty string fallback for skipped photos
      };

      // Remove id and undefined/null fields
      delete firestoreData.id;
      Object.keys(firestoreData).forEach(key => {
        if (firestoreData[key] === undefined || firestoreData[key] === null) {
          delete firestoreData[key];
        }
      });

      // Debug: Log the data being sent to Firestore
      console.log('🔍 Data being sent to Firestore:', JSON.stringify(firestoreData, null, 2));

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'users', finalPetData.userId!, 'petProfiles'), firestoreData);

      // Update with document ID
      finalPetData.id = docRef.id;

      this.state.isLoading = false;
      this.updateStep(OnboardingStep.COMPLETED);

      this.callbacks.onComplete?.(finalPetData as PetProfile);

      return finalPetData as PetProfile;

    } catch (error) {
      console.error('Error completing onboarding:', error);
      this.state.isLoading = false;
      this.state.error = error instanceof Error ? error.message : 'Failed to complete onboarding';
      this.callbacks.onError?.(this.state.error);
      throw error;
    }
  }

  /**
   * Cancels the onboarding process
   * @returns void
   */
  cancelOnboarding(): void {
    console.log('🔄 OnboardingService: cancelOnboarding called');
    this.callbacks.onCancel?.();
  }

  /**
   * Gets the current onboarding state
   * @returns OnboardingState
   */
  getState(): OnboardingState {
    return { ...this.state };
  }

  /**
   * Checks if user has completed onboarding before
   * @param userId - Firebase Auth user ID
   * @returns Promise<boolean>
   */
  async hasCompletedOnboarding(userId: string): Promise<boolean> {
    try {
      const petProfilesRef = collection(db, 'users', userId, 'petProfiles');
      const querySnapshot = await getDocs(petProfilesRef);

      // Check if any pet profile has onboardingCompleted: true
      for (const doc of querySnapshot.docs) {
        const data = doc.data();
        if (data.onboardingCompleted === true) {
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      return false;
    }
  }

  // Private helper methods

  private async convertFileToCompressedDataURL(file: File, maxWidth: number = 600, quality: number = 0.7): Promise<string> {
    console.log('🔄 Converting file to compressed data URL:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });
    
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth || height > maxWidth) {
          if (width > height) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          } else {
            width = (width * maxWidth) / height;
            height = maxWidth;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          const compressedDataURL = canvas.toDataURL('image/jpeg', quality);
          console.log('✅ File converted to compressed data URL successfully, length:', compressedDataURL.length);
          URL.revokeObjectURL(img.src);
          resolve(compressedDataURL);
        } else {
          URL.revokeObjectURL(img.src);
          reject(new Error('Failed to get 2d context for canvas'));
        }
      };
      
      img.onerror = (error) => {
        console.error('❌ Image load error:', error);
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image for compression'));
      };
      
      img.src = URL.createObjectURL(file);
    });
  }

  private async uploadImageToStorage(file: File): Promise<string> {
    const userId = this.state.petData.userId!;
    const timestamp = Date.now();
    const fileName = `pet-photos/${userId}/${timestamp}_${file.name}`;

    console.log('📤 Uploading image to Firebase Storage:', {
      fileName,
      fileSize: file.size,
      fileType: file.type,
      userId
    });

    try {
      const storageRef = ref(storage, fileName);
      
      // Add timeout and retry logic
      const uploadTask = uploadBytes(storageRef, file);
      
      // Wait for upload with timeout
      const snapshot = await Promise.race([
        uploadTask,
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Upload timeout after 30 seconds')), 30000)
        )
      ]) as any;

      console.log('✅ Image uploaded successfully:', snapshot.ref.fullPath);
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('✅ Download URL generated:', downloadURL);
      
      return downloadURL;
    } catch (error) {
      console.error('❌ Image upload failed:', error);
      
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('retry-limit-exceeded')) {
          throw new Error('Image upload failed due to network issues. Please check your internet connection and try again.');
        } else if (error.message.includes('permission-denied')) {
          throw new Error('Permission denied. Please make sure you are signed in and try again.');
        } else if (error.message.includes('storage/unauthorized')) {
          throw new Error('Unauthorized access. Please sign out and sign in again.');
        } else if (error.message.includes('Upload timeout')) {
          throw new Error('Upload timed out. Please try again with a smaller image or better internet connection.');
        }
      }
      
      throw error;
    }
  }

  private applyAnalysisResults(result: ImageAnalysisResult): void {
    // Calculate birthday from age estimate
    let calculatedBirthday: any = undefined;
    if (result.age && result.age > 0) {
      try {
        calculatedBirthday = calculateBirthdayFromAge(result.age);
        console.log('🎂 Calculated birthday from age:', {
          age: result.age,
          birthday: calculatedBirthday.toDate().toLocaleDateString(),
          note: 'User can edit this in their profile later'
        });
      } catch (error) {
        console.warn('⚠️ Failed to calculate birthday from age:', error);
      }
    }

    this.state.petData = {
      ...this.state.petData,
      breed: result.breed,
      age: result.age,
      birthday: calculatedBirthday, // Set calculated birthday
      gender: result.sex,
      aiAnalysisData: {
        breedConfidence: result.confidence,
        ageConfidence: result.ageConfidence,
        sexConfidence: result.sexConfidence,
        analysisDate: new Date(), // Use regular Date in state
        analysisImageUrl: result.analysisImageUrl,
        description: result.description // Enthusiastic compliment about pet's appearance
      }
    };
  }

  private updateStep(step: OnboardingStep): void {
    this.state.currentStep = step;

    // Update conversation context
    if (this.conversationContext) {
      this.conversationContext.currentStep = step;
    }

    this.callbacks.onStepChange?.(step);
  }

  /**
   * Processes user message through AI conversation
   * @param message - User's message
   * @returns Promise<any> - AI response
   */
  async processUserMessage(message: string): Promise<any> {
    if (!this.conversationContext) {
      throw new Error('Conversation not initialized');
    }

    try {
      const response = await processOnboardingMessage(message, this.conversationContext);

      // Update context with any extracted data
      if (response.extractedData) {
        this.state.petData = {
          ...this.state.petData,
          ...response.extractedData
        };
        this.conversationContext.petData = this.state.petData;
      }

      // Update step if specified
      if (response.nextStep) {
        this.updateStep(response.nextStep);
      }

      return response;

    } catch (error) {
      console.error('Error processing user message:', error);
      throw error;
    }
  }

  /**
   * Resets the onboarding process
   * @returns void
   */
  reset(): void {
    this.state = {
      currentStep: OnboardingStep.WELCOME,
      petData: {},
      userLanguage: 'en',
      isLoading: false
    };

    this.conversationContext = null;
  }
}

// Factory function for creating onboarding service instances
export const createOnboardingService = (
  config?: Partial<OnboardingConfig>,
  callbacks?: OnboardingCallbacks
): OnboardingService => {
  return new OnboardingService(config, callbacks);
};

// Check if user has completed onboarding
export const hasCompletedOnboarding = async (userId: string): Promise<boolean> => {
  try {
    console.log('🔍 hasCompletedOnboarding: Checking for user:', userId);
    const petProfilesRef = collection(db, 'users', userId, 'petProfiles');
    console.log('🔍 hasCompletedOnboarding: Collection path:', petProfilesRef.path);
    
    const querySnapshot = await getDocs(petProfilesRef);
    console.log('🔍 hasCompletedOnboarding: Found', querySnapshot.docs.length, 'pet profiles');

    // Check if any pet profile has onboardingCompleted: true
    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      console.log('🔍 hasCompletedOnboarding: Checking pet profile:', doc.id, 'onboardingCompleted:', data.onboardingCompleted);
      if (data.onboardingCompleted === true) {
        console.log('✅ hasCompletedOnboarding: Found completed onboarding for pet:', doc.id);
        return true;
      }
    }

    console.log('❌ hasCompletedOnboarding: No completed onboarding found');
    return false;
  } catch (error) {
    console.error('❌ hasCompletedOnboarding: Error checking onboarding status:', error);
    return false;
  }
};

// Utility functions for managing multiple pets
export const getUserPets = async (userId: string): Promise<PetProfile[]> => {
  try {
    const querySnapshot = await getDoc(doc(db, 'users', userId));
    if (!querySnapshot.exists()) {
      return [];
    }

    // In a real implementation, you'd query the petProfiles subcollection
    // For now, return empty array
    return [];
  } catch (error) {
    console.error('Error fetching user pets:', error);
    return [];
  }
};

export const updatePetProfile = async (
  userId: string,
  petId: string,
  updates: Partial<PetProfile>
): Promise<void> => {
  try {
    const petRef = doc(db, 'users', userId, 'petProfiles', petId);
    await updateDoc(petRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating pet profile:', error);
    throw error;
  }
};
